import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_LABORATORYFINANCE_LOAD_BY_LABORATORY_ID,
    ACTION_LABORATORYFINANCE_LOAD_BY_ID,
    ACTION_LABORATORYFINANCE_LOAD_FAILURE,
    ACTION_LABORATORYFINANCE_LOAD_SUCCESS,

    ACTION_LABORATORYFINANCE_SAVE,
    ACTION_LABORATORYFINANCE_SAVE_SUCCESS,
    ACTION_LABORATORYFINANCE_SAVE_FAILURE,

    ACTION_LABORATORYFINANCE_VALIDATE,
    ACTION_LABORATORYFINANCE_VALIDATE_SUCCESS,
    ACTION_LABORATORYFINANCE_VALIDATE_FAILURE,

    ACTION_LABORATORYFINANCE_CREATE,
    ACTION_LABORATORYFINANCE_CREATE_SUCCESS,
    ACTION_LABORATORYFINANCE_CREATE_FAILURE,

    ACTION_LABORATORYFINANCE_SYNC,
    ACTION_LABORATORYFINANCE_SYNC_SUCCESS,
    ACTION_LABORATORYFINANCE_SYNC_FAILURE,

    ACTION_LABORATORYFINANCE_CLEAR,
    ACTION_LABORATORYFINANCE_FILTER,

    LaboratoryFinanceAction
} from '../actions/laboratoryFinance';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ILaboratoryFinanceState {
    formData: Dtos.LaboratoryFinance | Dtos.LaboratoryFinance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.LaboratoryFinance[];
    formLength: number;
}

const initialState: ILaboratoryFinanceState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ILaboratoryFinanceState, _isLoading: boolean): ILaboratoryFinanceState {
    const newState: ILaboratoryFinanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ILaboratoryFinanceState,
    formData: Dtos.LaboratoryFinance | Dtos.LaboratoryFinance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ILaboratoryFinanceState {
    const newState: ILaboratoryFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ILaboratoryFinanceState, responseStatus: Dtos.ResponseStatus): ILaboratoryFinanceState {
    const newState: ILaboratoryFinanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ILaboratoryFinanceState, formFilter: IFormFilter): ILaboratoryFinanceState {

    const items: Dtos.LaboratoryFinance[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.taxInvoiceName.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: ILaboratoryFinanceState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ILaboratoryFinanceState, propertiesToClear?: string[]): ILaboratoryFinanceState {

    if (!propertiesToClear) {
        const newState: ILaboratoryFinanceState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ILaboratoryFinanceState, _isSaving: boolean): ILaboratoryFinanceState {
    const newState: ILaboratoryFinanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ILaboratoryFinanceState,
    formData: Dtos.LaboratoryFinance | Dtos.LaboratoryFinance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ILaboratoryFinanceState {
    const newState: ILaboratoryFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ILaboratoryFinanceState, responseStatus: Dtos.ResponseStatus): ILaboratoryFinanceState {
    const newState: ILaboratoryFinanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: ILaboratoryFinanceState, _isValidating: boolean): ILaboratoryFinanceState {
    const newState: ILaboratoryFinanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ILaboratoryFinanceState,
    formData: Dtos.LaboratoryFinance | Dtos.LaboratoryFinance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ILaboratoryFinanceState {
    const newState: ILaboratoryFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ILaboratoryFinanceState, responseStatus: Dtos.ResponseStatus): ILaboratoryFinanceState {
    const newState: ILaboratoryFinanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ILaboratoryFinanceState, _isCreating: boolean): ILaboratoryFinanceState {
    const newState: ILaboratoryFinanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ILaboratoryFinanceState,
    formData: Dtos.LaboratoryFinance,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ILaboratoryFinanceState {
    const newState: ILaboratoryFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ILaboratoryFinanceState, responseStatus: Dtos.ResponseStatus): ILaboratoryFinanceState {
    const newState: ILaboratoryFinanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

const LaboratoryFinanceReducer: Reducer<ILaboratoryFinanceState> = (state: ILaboratoryFinanceState = initialState, action: LaboratoryFinanceAction) => {
    switch (action.type) {
        case ACTION_LABORATORYFINANCE_LOAD_BY_LABORATORY_ID:
        case ACTION_LABORATORYFINANCE_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_LABORATORYFINANCE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_LABORATORYFINANCE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_LABORATORYFINANCE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_LABORATORYFINANCE_SAVE:
            return saving(state, true);
        case ACTION_LABORATORYFINANCE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_LABORATORYFINANCE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_LABORATORYFINANCE_VALIDATE:
            return validating(state, true);
        case ACTION_LABORATORYFINANCE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_LABORATORYFINANCE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_LABORATORYFINANCE_CREATE:
            return creating(state, true);
        case ACTION_LABORATORYFINANCE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_LABORATORYFINANCE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_LABORATORYFINANCE_FILTER:
            return filter(state, action.formFilter);
        case ACTION_LABORATORYFINANCE_SYNC:
            return loading(state, true);
        case ACTION_LABORATORYFINANCE_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_LABORATORYFINANCE_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default LaboratoryFinanceReducer;